import * as React from 'react';
import { useRef, useState } from 'react';
import { useAppContext } from '../../Services/AppContext';
import Form from '../Form/Form';
import FormControl from '../Form/FormControl';
import TextArea from '../Base/TextArea/TextArea';
import SubmitButton from '../Button/SubmitButton';
import ApiClient from '../../Services/ApiClient';
import apiClient from '../../Services/ApiClient';
import withForm from '../Form/withForm';
import { useFormContext } from '../Form/FormContext';
import FileUpload from '../Base/FileUpload/FileUpload';
import { v4 as uuidv4 } from 'uuid';
// @ts-ignore
import styles from './CaseChatCreateMessage.module.scss';
import { joinClasses } from '../../Utils/className.util';
import CaseChatCreateMessageFilePreview from './CaseChatCreateMessageFilePreview';
import { PhraseType, User } from '../../graphql/graphql';
import Text from '../Base/Text/Text';
import { PredefinedPhrase } from '../../Modules/PredefinedPhrases/Components/PredefinedPhrasesSetup/PredefinedPhrasesList';
import PredefinedPhraseSelectionModal from '../../Modules/PredefinedPhrases/Components/PredefinedPhraseSelection/PredefinedPhraseSelection/PredefinedPhraseSelectionModal';
import useTranslation from '../../Hooks/useTranslation';
import FirstUpper from '../FirstUpper';
import { Position } from '../../Modules/Base/Enums/Position.enum';
import useApiClient from '../../Modules/Base/Hooks/useApiClient';

interface CaseMessageValues {
	message: string;
}

interface CaseChatCreateMessageProps {
	userId: string;
	caseId: string;
	beforeMessage: () => void;
	onMessage: () => void;
	patient?: User;
	patientId: string;
}

const CaseChatCreateMessage = (props: CaseChatCreateMessageProps) => {
	const { translation } = useAppContext();
	const { userId, caseId, onMessage, beforeMessage, patient, patientId } =
		props;
	const { formMethods } = useFormContext();
	const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
	const [inputVisible, setInputVisible] = useState(true);
	const [isMenuOpen, setIsMenuOpen] = useState(false);
	const [isPredefinedPhraseSelectionOpen, setIsPredefinedPhraseSelectionOpen] =
		useState(false);
	const { t } = useTranslation();
	const { displayError } = useApiClient();

	const handleLoadPredefinedPhrase = (item: PredefinedPhrase) => {
		setIsPredefinedPhraseSelectionOpen(false);
		formMethods.setValue('message', item.value);
	};

	const handleOpenPredefinedPhraseSelection = () => {
		setIsMenuOpen(false);
		setIsPredefinedPhraseSelectionOpen(true);
	};

	const pickExt = (file: File): string | undefined => {
		const dot = file.name.lastIndexOf('.');
		if (dot > -1 && dot < file.name.length - 1) {
			return file.name.slice(dot + 1).toLowerCase();
		}
		const mimePart = file.type.split('/')[1];
		return mimePart ? mimePart.toLowerCase() : undefined;
	};

	const handleSendFiles = async (): Promise<void> => {
		if (!selectedFiles.length) {
			return;
		}

		try {
			for (const file of selectedFiles as File[]) {
				//GET FILE EXTENSION
				const ext = pickExt(file);
				if (!ext) {
					return;
				}

				const fileId = uuidv4();
				const fileIdExt = `${fileId}.${ext}`;
				const isImage = file.type.startsWith('image/');
				const msgType = isImage ? 'p' : ('f' as 'p' | 'f');

				// CREATE MESSAGE
				await apiClient.put(`/cases/${caseId}/message`, {
					text: fileIdExt,
					sender: userId,
					type: msgType,
					initial: false,
				});

				// UPLOAD FILE
				const formData = new FormData();
				formData.append('files[]', file, file.name);
				await apiClient.postMultipart(
					`/cases/${caseId}/message/${fileIdExt}/upload`,
					formData,
				);
			}
		} catch (e) {
			displayError(e);
		} finally {
			setSelectedFiles([]);
		}
	};

	const handleSendMessage = async (values: CaseMessageValues) => {
		if (!values.message && !selectedFiles.length) {
			return;
		}

		beforeMessage();

		if (selectedFiles.length) {
			await handleSendFiles();
		}

		if (values.message) {
			await ApiClient.put('/cases/' + caseId + '/message', {
				text: values.message,
				sender: userId,
				type: 'm',
				initial: false,
			});
			formMethods.setValue('message', '');
			setInputVisible(false);
			setInputVisible(true);
		}
		onMessage();
	};

	const handleAddFile = (event: React.ChangeEvent<HTMLInputElement>) => {
		setIsMenuOpen(false);
		const files = event.target.files;
		if (files) {
			setSelectedFiles(prev => [...prev, files[0]]);
		}
	};

	const removeFile = (file: File) => {
		setSelectedFiles(prev => prev.filter(item => item !== file));
	};

	return (
		<>
			<Form>
				<div className={styles.filesWrapper}>
					{selectedFiles.map((file: File, index: number) => (
						<CaseChatCreateMessageFilePreview
							removeFile={removeFile}
							selectedFile={file}
							key={index}
						/>
					))}
				</div>
				<div className={styles.wrapper}>
					{patient?.profile?.languageFormatted && (
						<div className={styles.chatLanguage}>
							<Text tx='base.chatLanguage' firstUpper colon inline />{' '}
							{patient.profile.languageFormatted}
						</div>
					)}
					<div className={styles.inputWrapper}>
						<div
							className={joinClasses([
								'create-case-message__wrapper',
								styles.messageWrapper,
							])}>
							<div className='create-case-message__input-wrapper'>
								<div className='v2-forms'>
									<div className='form-group'>
										{inputVisible && (
											<FormControl
												name={'message'}
												wrapperClassName={''}
												hideRequired={true}>
												<TextArea
													rows={1}
													placeholder={
														translation[
															'component.createCaseMessage.messagePlaceholder'
														]
													}
													captureAudio={{
														allow: true,
														position: Position.LEFT,
													}}
													wrapperClassName={''}
													className={styles.textArea}
													style={{ paddingRight: '60px' }}
												/>
											</FormControl>
										)}
									</div>
								</div>
							</div>
							<div className='create-case-message__button-wrapper'>
								<SubmitButton
									onSubmit={values =>
										handleSendMessage(values as unknown as CaseMessageValues)
									}
									className={'create-case-message__button-wrapper__button'}
									title={''}
								/>
							</div>
						</div>
						<div className={styles.subMenu}>
							<div
								className={styles.plusButton}
								onClick={() => setIsMenuOpen(true)}
							/>
							{isMenuOpen && (
								<div className={styles.menu}>
									<div className={styles.inputGroup}>
										<div className={styles.customFile}>
											<div className={styles.fileUpload}>
												<FileUpload
													id='chat-upload-file'
													onChange={handleAddFile}
												/>
											</div>
											<label
												className={styles.attachment}
												htmlFor='chat-upload-file'>
												<FirstUpper>{t('base.shared.pickFile')}</FirstUpper>
											</label>
										</div>
									</div>
									<div
										className={styles.phrasesButton}
										onClick={handleOpenPredefinedPhraseSelection}>
										<div className={styles.icon} />
										<Text tx='base.predefinedPhrases' />
									</div>
									<div
										className={styles.close}
										onClick={() => setIsMenuOpen(false)}
									/>
								</div>
							)}
						</div>
						{isPredefinedPhraseSelectionOpen && (
							<PredefinedPhraseSelectionModal
								isOpen
								type={PhraseType.Chat}
								onSelect={handleLoadPredefinedPhrase}
								onClose={() => setIsPredefinedPhraseSelectionOpen(false)}
							/>
						)}
					</div>
				</div>
			</Form>
		</>
	);
};

export default withForm(CaseChatCreateMessage);
